/**
 * Client context hooks — active client selector, persisted in localStorage.
 * GET /api/clients → bare List[ClientListItem] (clients_router.py:229) — no {items,total} wrapper.
 */

import { createContext, useContext } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clientsApi } from '@/api/client'
import type { Client, ClientListItem } from '@/types'

const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

const LS_KEY = 'stars_active_client_id'

// ---- Mock data — field names match backend ClientListItem exactly ----

const MOCK_CLIENTS: ClientListItem[] = [
  {
    id: 'tenant-touchbrand',
    name: 'TouchBrand (internal)',
    status: 'active',
    current_stage: 'active',
    channel: null,
    counts: { leads: 0, deals: 3 },
  },
  {
    id: 'tenant-vet',
    name: 'Ветеринарные клиники Петрова',
    status: 'active',
    current_stage: 'build',
    channel: 'telegram',
    counts: { leads: 0, deals: 1 },
  },
  {
    id: 'tenant-stomatology',
    name: 'Стоматология Соколовой',
    status: 'prospect',
    current_stage: 'new_lead',
    channel: null,
    counts: { leads: 1, deals: 1 },
  },
]

// ---- Query keys ----

export const clientKeys = {
  all: ['clients'] as const,
  list: () => ['clients', 'list'] as const,
  detail: (id: string) => ['clients', id] as const,
}

// ---- Hooks ----

export function useClientsList() {
  return useQuery<ClientListItem[]>({
    queryKey: clientKeys.list(),
    queryFn: () => {
      if (USE_MOCK) return Promise.resolve(MOCK_CLIENTS)
      // Бэкенд возвращает голый список ClientListItem[] (clients_router.py:229).
      return clientsApi.list()
    },
    staleTime: 60_000,
  })
}

export function useCreateClient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: { name: string; channel?: string }) => {
      if (USE_MOCK) {
        const newClient: Client = {
          id: `tenant-${Date.now()}`,
          name: payload.name,
          status: 'prospect',
          current_stage: null,
          channel: payload.channel ?? null,
          counts: { leads: 0, deals: 0 },
          config: {},
          launched_at: null,
          created_at: new Date().toISOString(),
        }
        MOCK_CLIENTS.push(newClient)
        return Promise.resolve(newClient)
      }
      return clientsApi.create(payload)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clientKeys.list() })
    },
  })
}

// ---- Active client context ----

export interface ActiveClientContextValue {
  activeClientId: string | null
  setActiveClientId: (id: string | null) => void
}

export const ActiveClientContext = createContext<ActiveClientContextValue>({
  activeClientId: null,
  setActiveClientId: () => {},
})

export function useActiveClient() {
  return useContext(ActiveClientContext)
}

export function getStoredClientId(): string | null {
  try {
    return localStorage.getItem(LS_KEY)
  } catch {
    return null
  }
}

export function storeClientId(id: string | null) {
  try {
    if (id) {
      localStorage.setItem(LS_KEY, id)
    } else {
      localStorage.removeItem(LS_KEY)
    }
  } catch {
    // ignore
  }
}
